import { TooltipProps } from "@blueprintjs/core";
import { HexColor } from "@hex/common";
import classNames from "classnames";
import { rgba } from "polished";
import React from "react";
import styled, { css } from "styled-components";

import { HexTooltip } from "../../../hex-components";
import { HexToolTipProps } from "../../../hex-components/HexTooltip";
import { IconsToNames } from "../../../hex-components/IconPicker.js";
import {
  MinimalStatus,
  SchemaStatusSource,
} from "../../../hooks/useProjectLabelsForHex";
import {
  ChexagonIcon,
  FolderIcon,
  SQLCellIcon,
  SingleChevronDownIcon,
} from "../../icons/CustomIcons.js";

import { StatusIcon } from "./OrganizationIcons";

// --- Provider ---

type ContextValue = {
  status: Record<string, unknown> & Omit<MinimalStatus, "id" | "inLibrary">;
  inheritedFrom: SchemaStatusSource | null;
  minimal: boolean;
  condensed: boolean;
  enforcesReview: boolean;
  resourceType: "hex" | "data" | null;
};

const Context = React.createContext<ContextValue | null>(null);

const useContext = (): NonNullable<ContextValue> => {
  const context = React.useContext(Context);
  if (context == null) {
    throw new Error("StatusLabel must be used within a StatusLabelProvider");
  }
  return context;
};

export interface StatusLabelProviderProps extends Omit<
  React.ComponentProps<typeof Context.Provider>,
  "value"
> {
  status: ContextValue["status"];
  inheritedFrom?: ContextValue["inheritedFrom"];
  minimal?: boolean;
  condensed?: boolean;
  enforcesReview?: boolean;
  resourceType?: "hex" | "data" | null;
}

const Provider = React.memo(function Provider(props: StatusLabelProviderProps) {
  const {
    condensed = false,
    enforcesReview = false,
    inheritedFrom = null,
    minimal = false,
    resourceType = null,
    status,
    ...rest
  } = props;
  const value = React.useMemo(
    () => ({
      condensed,
      enforcesReview,
      inheritedFrom,
      minimal,
      resourceType,
      status,
    }),
    [condensed, enforcesReview, inheritedFrom, minimal, resourceType, status],
  );
  return <Context.Provider value={value} {...rest} />;
});

// --- Label ---

const StatusName = styled.div``;

const _StatusLabelWrapper = styled.div<{
  $condensed?: boolean;
  $minimal?: boolean;
  $muted?: boolean;
}>`
  display: flex;
  align-items: center;
  min-width: 0;

  color: ${({ $muted, theme }) =>
    $muted ? theme.fontColor.MUTED : theme.fontColor.DEFAULT};

  ${({ $condensed }) =>
    $condensed
      ? css`
          height: 18px;
          font-size: ${({ theme }) => theme.fontSize.SMALL};
          gap: 2px;

          ${StatusIcon} {
            height: 12px;
            width: 12px;
          }
        `
      : css`
          height: 22px;
          font-size: ${({ theme }) => theme.fontSize.SMALL};
          padding: 4px 6px;
          gap: 4px;
        `}

  ${({ $condensed, $minimal }) =>
    $minimal &&
    css`
      justify-content: center;

      ${$condensed
        ? css`
            width: 18px;

            svg {
              height: 14px;
              width: 14px;
            }
          `
        : css`
            width: 22px;
          `}
    `};
`;

export const StatusLabelWrapper = React.memo(
  React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<typeof _StatusLabelWrapper>
  >(function StatusLabelWrapper(props, ref) {
    return <_StatusLabelWrapper ref={ref} {...props} />;
  }),
);

const _EndorsedStatusLabelWrapper = styled(StatusLabelWrapper)<{
  $condensed?: boolean;
  $minimal?: boolean;
  $color: HexColor;
}>`
  ${({ $color, theme }) => `
    background-color: ${rgba(theme.statusCategoryColor[$color], 0.1)};
    color: ${theme.statusCategoryColor[$color]};

    svg {
      color: ${rgba(theme.statusCategoryColor[$color], 0.85)};
    }
  `}

  border-radius: 20px;

  max-width: min-content;
  white-space: nowrap;

  ${({ $condensed, $minimal }) =>
    $minimal &&
    css`
      justify-content: center;

      ${$condensed
        ? css`
            width: 18px;
            height: 18px;

            svg {
              height: 14px;
              width: 14px;
            }
          `
        : css`
            width: 22px;
            height: 22px;
          `}
    `}

  ${({ $condensed, $minimal }) =>
    !$minimal &&
    ($condensed
      ? css`
          padding: 1px 5px 1px 3px;
          gap: 2px;

          svg {
            height: 14px;
            width: 14px;
          }
        `
      : css`
          padding: 4px 6px 4px 4px;
          gap: 2px;
        `)}

  ${({ $muted }) =>
    $muted &&
    css`
      background: none;
      padding-left: 0px;
    `}
`;

export const EndorsedStatusLabelWrapper = React.memo(
  React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<typeof _EndorsedStatusLabelWrapper>
  >(function EndorsedStatusLabelWrapper(props, ref) {
    return <_EndorsedStatusLabelWrapper ref={ref} {...props} />;
  }),
);

export type StatusLabelLabelProps = React.ComponentPropsWithoutRef<
  typeof _StatusLabelWrapper
> & { muted?: boolean; showChevron?: boolean; className?: string };

const Label = React.memo(
  React.forwardRef<HTMLDivElement, StatusLabelLabelProps>(function StatusTag(
    { className, muted, showChevron, ...rest },
    ref,
  ) {
    const { condensed, minimal, status } = useContext();
    const { color, endorsed, icon } = status;

    if (endorsed) {
      return (
        <EndorsedStatusLabelWrapper
          ref={ref}
          $color={color}
          $condensed={condensed}
          $minimal={minimal}
          $muted={endorsed ? false : muted}
          className={className}
          {...rest}
        >
          {icon != null ? IconsToNames[icon] : <ChexagonIcon />}
          {!minimal && <StatusName>{status.name}</StatusName>}
          {showChevron && (
            <SingleChevronDownIcon css="margin-right: -3px;" iconSize={14} />
          )}
        </EndorsedStatusLabelWrapper>
      );
    }

    return (
      <StatusLabelWrapper
        ref={ref}
        $condensed={condensed}
        $minimal={minimal}
        $muted={muted}
        className={className}
        {...rest}
      >
        <StatusIcon $color={color} />
        {!minimal && <StatusName>{status.name}</StatusName>}
        {showChevron && (
          <SingleChevronDownIcon css="margin-right: -3px;" iconSize={14} />
        )}
      </StatusLabelWrapper>
    );
  }),
);

// --- Tooltip ---

const TooltipSubsection = styled.div<{ $withTopBorder: boolean }>`
  color: ${({ theme }) => theme.fontColor.MUTED};
  border-top: ${({ $withTopBorder, theme }) =>
    $withTopBorder ? `1px solid ${theme.borderColor.DEFAULT}` : "none"};
  padding: ${({ $withTopBorder }) =>
    `${$withTopBorder ? "3px" : "0"} 8px 0 8px`};
  margin: ${({ $withTopBorder }) =>
    `${$withTopBorder ? "4px" : "0"} -8px 0 -8px`};
`;

export interface StatusLabelTooltipProps extends Partial<HexToolTipProps> {}

const Tooltip = React.memo(function StatusLabelTooltip(
  props: StatusLabelTooltipProps,
) {
  const { disabled: disabledProp, ...rest } = props;

  const { enforcesReview, inheritedFrom, minimal, resourceType, status } =
    useContext();

  const hasDescription = status.description
    ? status.description.trim().length > 0
    : false;
  const showReviewEnforcement = enforcesReview && resourceType === "hex";
  const disabled =
    disabledProp ||
    (!minimal &&
      !hasDescription &&
      !status.endorsed &&
      !inheritedFrom &&
      !showReviewEnforcement);

  return (
    <HexTooltip
      content={
        <>
          {minimal && <div>{status.name}</div>}
          {status.description}
          {status.endorsed && (
            <TooltipSubsection $withTopBorder={minimal || hasDescription}>
              Endorsed by workspace admins and prioritized by the Hex agent
            </TooltipSubsection>
          )}
          {showReviewEnforcement && (
            <TooltipSubsection
              $withTopBorder={minimal || hasDescription || status.endorsed}
            >
              Review required for publishing
            </TooltipSubsection>
          )}
          {inheritedFrom && (
            <TooltipSubsection
              $withTopBorder={
                minimal ||
                hasDescription ||
                status.endorsed ||
                showReviewEnforcement
              }
            >
              Inherited from{" "}
              <span
                css={`
                  white-space: nowrap;
                `}
              >
                {inheritedFrom.__typename === "DataSourceSchema" ? (
                  <FolderIcon />
                ) : (
                  <SQLCellIcon />
                )}
                {inheritedFrom?.name}
              </span>
            </TooltipSubsection>
          )}
        </>
      }
      disabled={disabled}
      placement="bottom"
      {...rest}
    />
  );
});

// --- Composition ---

export interface StatusLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  status: Record<string, unknown> & Omit<MinimalStatus, "id" | "inLibrary">;
  /** Condensed form with smaller icon & less padding */
  condensed?: boolean;
  /** Icon-only form */
  minimal?: boolean;
  muted?: boolean;
  descriptionTooltipProps?: Omit<TooltipProps, "content" | "renderTarget">;
  inheritedFrom?: SchemaStatusSource;
  hideTooltip?: boolean;
  enforcesReview?: boolean;
  resourceType?: "hex" | "data" | null;
  showChevron?: boolean;
}

const Composition: React.ComponentType<StatusLabelProps> = React.memo(
  function StatusLabelComposition({
    className,
    condensed,
    descriptionTooltipProps,
    enforcesReview,
    hideTooltip,
    inheritedFrom,
    minimal,
    muted,
    resourceType,
    status,
    ...rest
  }: StatusLabelProps) {
    return (
      <Provider
        condensed={condensed}
        enforcesReview={enforcesReview}
        inheritedFrom={inheritedFrom}
        minimal={minimal}
        resourceType={resourceType}
        status={status}
      >
        <Tooltip
          disabled={hideTooltip}
          // eslint-disable-next-line react/jsx-no-bind -- rest props change every render
          renderTarget={({
            className: tooltipClassName,
            isOpen: _isOpen,
            ref: targetRef,
            ...targetProps
          }) => {
            return (
              <Label
                {...rest}
                {...targetProps}
                ref={targetRef}
                className={classNames(tooltipClassName, className)}
              />
            );
          }}
          {...descriptionTooltipProps}
        />
      </Provider>
    );
  },
);

/**
 * @example
 * <StatusLabel.Provider status minimal condensed inheritedFrom>
 *   <StatusLabel.Tooltip
 *     renderTarget={({ isOpen: _isOpen, ...tooltipTargetProps }) => (
 *       <StatusLabel.Label muted {...tooltipTargetProps} />
 *     )}
 *   />
 * </StatusLabel.Provider>
 */
export const StatusLabel = Object.assign(Composition, {
  Label,
  Tooltip,
  Provider,
});
